import { Box, Paper, Skeleton, Typography } from '@mui/material';
import type { KpiResponse } from '~/apis/analyticsApi';
import { getKpiAccentColors, type KpiCardConfig } from '../types';

type KpiCardProps = {
  config: KpiCardConfig;
  data: KpiResponse | null;
  loading?: boolean;
};

function formatNumber(value: number | undefined): string {
  return value == null ? '—' : value.toLocaleString();
}

function formatPercentage(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function KpiCard({ config, data, loading = false }: KpiCardProps) {
  const colors = getKpiAccentColors(config.accent);
  const primary = data?.[config.key];
  const secondary = config.comparison ? data?.[config.comparison.secondaryKey] : undefined;
  const percentage = config.comparison ? data?.[config.comparison.percentageKey] : undefined;

  const mainDisplay = config.comparison
    ? `${formatNumber(primary)} vs ${formatNumber(secondary)}`
    : formatNumber(primary);

  const subtext = config.comparison
    ? `${formatPercentage(percentage)} saleable · ${config.subtext}`
    : config.subtext;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.bg,
          color: colors.main,
          mb: 1.5,
        }}
      >
        {config.icon}
      </Box>
      {loading ? (
        <Skeleton variant="text" width="70%" height={40} sx={{ mb: 0.5 }} />
      ) : (
        <Typography
          variant={config.comparison ? 'h5' : 'h4'}
          component="p"
          sx={{
            fontWeight: 700,
            color: colors.main,
            lineHeight: 1.2,
            mb: 0.5,
            wordBreak: 'break-word',
          }}
        >
          {mainDisplay}
        </Typography>
      )}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {config.label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {loading && config.comparison ? config.subtext : subtext}
      </Typography>
    </Paper>
  );
}
